import { useEffect, useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Link, useNavigate } from "react-router-dom";
import { ArrowLeft, MapPin, Lock, ShieldCheck, Loader2, PackageSearch } from "lucide-react";
import { useAuth } from "@/hooks/useAuth";
import { SignInModal } from "@/components/auth/SignInModal";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const KIND_LABEL = { load: "Load", relief: "Relief", rv: "RV Job" };
const KIND_COLOR = { load: "bg-rr-orange", relief: "bg-rr-red", rv: "bg-[#2E6FD9]" };
const CARD_INITIAL = { opacity: 0, y: 10 };
const CARD_ANIMATE = { opacity: 1, y: 0 };

export default function Opportunities() {
  const navigate = useNavigate();
  const user = useAuth();
  const [opportunities, setOpportunities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [billing, setBilling] = useState(null);
  const [signInOpen, setSignInOpen] = useState(false);
  const [acceptingId, setAcceptingId] = useState(null);

  useEffect(() => {
    axios.get(`${API}/opportunities`).then((r) => setOpportunities(r.data.opportunities || [])).finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (user) {
      axios.get(`${API}/billing/me`).then((r) => setBilling(r.data)).catch(() => {});
    }
    // Intentionally keyed on user?.email only, to avoid re-fetching on every user object reference change
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.email]);

  const isSubscribed = billing?.status === "active";
  const isVerified = !!user?.verified;
  const canAccept = user && isSubscribed && isVerified;

  const handleAccept = async (opp) => {
    if (!user) {
      setSignInOpen(true);
      return;
    }
    if (!isSubscribed) {
      toast.error("An active RelayRigs Membership is required to accept opportunities.");
      navigate("/account");
      return;
    }
    if (!isVerified) {
      toast.error("Your driver verification must be approved before accepting opportunities.");
      return;
    }
    setAcceptingId(opp.id);
    try {
      await axios.post(`${API}/opportunities/${opp.kind}/${opp.id}/accept`, {});
      toast.success("Opportunity accepted! Check the RelayRigs app to continue.");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not accept this opportunity.");
    } finally {
      setAcceptingId(null);
    }
  };

  return (
    <div data-testid="opportunities-page" className="min-h-screen bg-rr-surface pt-28 pb-20 px-5">
      <div className="max-w-5xl mx-auto">
        <Link to="/" data-testid="opportunities-back-link" className="inline-flex items-center gap-2 text-sm font-bold text-rr-navy mb-6">
          <ArrowLeft size={16} /> Back to RelayRigs
        </Link>

        <h1 className="font-heading text-3xl md:text-4xl font-extrabold text-rr-navy">Find Driving Opportunities</h1>
        <p className="text-rr-muted mt-2 max-w-xl">
          Browse live loads, relief requests, and RV jobs. Accepting a job requires an active RelayRigs Membership and a verified driver profile.
        </p>

        {!canAccept && (
          <div data-testid="opportunities-gate-banner" className="mt-6 flex items-start gap-3 bg-white border border-rr-line rounded-xl p-4">
            <Lock size={18} className="text-rr-orange mt-0.5 shrink-0" />
            <div className="text-sm text-rr-navy">
              {!user && "Sign in to view your eligibility and accept opportunities."}
              {user && !isSubscribed && "Subscribe to a RelayRigs Membership to accept opportunities."}
              {user && isSubscribed && !isVerified && "Your driver verification is still pending approval in the app."}
            </div>
          </div>
        )}

        <div
          data-testid="opportunities-map-canvas"
          className="mt-8 relative h-[280px] rounded-[2rem] overflow-hidden border border-rr-line bg-[radial-gradient(circle_at_30%_20%,#E9F0F5,#F4F7FA_60%)] flex items-center justify-center"
        >
          <div className="text-center text-rr-muted text-sm">
            <MapPin className="mx-auto mb-2 text-rr-orange" size={22} />
            {opportunities.length > 0 ? `${opportunities.length} live opportunities on the map` : "Opportunities will appear here as they're posted"}
          </div>
        </div>

        <div className="mt-8">
          {loading ? (
            <div className="flex justify-center py-12"><Loader2 className="animate-spin text-rr-orange" size={28} /></div>
          ) : opportunities.length === 0 ? (
            <div data-testid="opportunities-empty-state" className="text-center py-16 bg-white rounded-2xl border border-rr-line">
              <PackageSearch className="mx-auto text-rr-muted mb-3" size={32} />
              <p className="text-rr-navy font-bold">No live opportunities right now</p>
              <p className="text-rr-muted text-sm mt-1">Check back soon — new loads, relief requests, and RV jobs post regularly.</p>
            </div>
          ) : (
            <div className="grid gap-4">
              {opportunities.map((opp) => (
                <motion.div
                  key={`${opp.kind}-${opp.id}`}
                  initial={CARD_INITIAL}
                  animate={CARD_ANIMATE}
                  data-testid={`opportunity-card-${opp.id}`}
                  className="bg-white rounded-xl border border-rr-line p-5 flex items-center justify-between gap-4"
                >
                  <div>
                    <span className={`text-[10px] font-bold text-white px-2 py-0.5 rounded-full ${KIND_COLOR[opp.kind]}`}>{KIND_LABEL[opp.kind]}</span>
                    <div className="font-heading font-bold text-rr-navy mt-2">{opp.origin || "Origin TBD"} → {opp.destination || "Destination TBD"}</div>
                    <div className="text-xs text-rr-muted mt-1">{opp.mileage ? `${opp.mileage} mi` : ""} {opp.equipment ? `• ${opp.equipment}` : ""} {opp.compensation ? `• $${opp.compensation}` : ""}</div>
                  </div>
                  <button
                    onClick={() => handleAccept(opp)}
                    disabled={acceptingId === opp.id}
                    data-testid={`opportunity-accept-${opp.id}`}
                    className={`shrink-0 flex items-center gap-2 text-sm font-bold px-5 py-2.5 rounded-full transition-colors ${
                      canAccept ? "bg-rr-orange text-white hover:bg-rr-navy" : "bg-rr-surface text-rr-muted border border-rr-line"
                    }`}
                  >
                    {acceptingId === opp.id ? <Loader2 size={14} className="animate-spin" /> : canAccept ? <ShieldCheck size={14} /> : <Lock size={14} />}
                    Accept
                  </button>
                </motion.div>
              ))}
            </div>
          )}
        </div>
      </div>
      <SignInModal open={signInOpen} onOpenChange={setSignInOpen} />
    </div>
  );
}
